import client from '@/graphql/client'
import CreateOrganisation from '@/graphql/mutations/CreateOrganisation'
import DeleteOrganisation from '@/graphql/mutations/DeleteOrganisation'
import ArchiveOrganisation from '@/graphql/mutations/ArchiveOrganisation'
import RestoreOrganisation from '@/graphql/mutations/RestoreOrganisation'
import PurgeOrganisation from '@/graphql/mutations/PurgeOrganisation'
import ListOrganisations from '@/graphql/queries/ListOrganisations'
import UpdateOrganisationAiSettings from '@/graphql/mutations/UpdateOrganisationAiSettings'
import AdminUpdateOrganisationAiSettings from '@/graphql/mutations/AdminUpdateOrganisationAiSettings'
import AdminUpdateOrganisationAccount from '@/graphql/mutations/AdminUpdateOrganisationAccount'

export const organisations = {
  namespaced: true,
  state: {
    organisations: null
  },
  mutations: {
    setOrganisations (state, payload) {
      state.organisations = payload
    },
    addOrUpdateOrganisation (state, payload) {
      const index = state.organisations?.findIndex((organisation) => organisation.id === payload.id)
      if (index >= 0) {
        state.organisations[index] = Object.assign(state.organisations[index], payload)
      } else {
        if (!state.organisations) { state.organisations = [] }
        state.organisations.push(payload)
      }
    },
    deleteOrganisation (state, payload) {
      const index = state.organisations.findIndex((organisation) => organisation.id === payload.id)
      if (index >= 0) {
        state.organisations.splice(index, 1)
      }
    }
  },
  actions: {
    async createOrganisation ({ commit }, { name }) {
      try {
        const { createOrganisation } = await client.query({
          operationName: 'createOrganisation',
          query: CreateOrganisation,
          variables: {
            name
          }
        })
        commit('addOrUpdateOrganisation', createOrganisation)
        commit('snackbar/showMessage', { content: 'Organization created' }, { root: true })
        return createOrganisation.id
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to create organization', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    },

    async updateOrganisation ({ commit }, { name, id }) {
      try {
        const { createOrganisation } = await client.query({
          operationName: 'createOrganisation',
          query: CreateOrganisation,
          variables: {
            id,
            name
          }
        })
        commit('addOrUpdateOrganisation', createOrganisation)
        commit('snackbar/showMessage', { content: 'Updated' }, { root: true })
        return createOrganisation.id
      } catch (e) {
        console.log(e)
      }
    },

    async deleteOrganisation ({ commit }, { id }) {
      try {
        const { deleteOrganisation } = await client.query({
          operationName: 'deleteOrganisation',
          query: DeleteOrganisation,
          variables: {
            id
          }
        })
        commit('deleteOrganisation', deleteOrganisation)
        commit('snackbar/showMessage', { content: 'Deleted' }, { root: true })
      } catch (e) {
        alert(e.message)
        console.log(e)
      }
    },

    // Soft-delete: archive the org (reversible). It stays in a SuperAdmin's list
    // (with deletedAt set) so it can be restored; the access gate + member listings
    // treat it as gone.
    async archiveOrganisation ({ commit }, { id }) {
      try {
        const { archiveOrganisation } = await client.query({
          operationName: 'archiveOrganisation',
          query: ArchiveOrganisation,
          variables: { id }
        })
        commit('addOrUpdateOrganisation', archiveOrganisation)
        commit('snackbar/showMessage', { content: 'Organization archived' }, { root: true })

        return archiveOrganisation
      } catch (e) {
        alert(e.message)
        console.log(e)
      }
    },

    async restoreOrganisation ({ commit }, { id }) {
      try {
        const { restoreOrganisation } = await client.query({
          operationName: 'restoreOrganisation',
          query: RestoreOrganisation,
          variables: { id }
        })
        // restore clears deletedAt (resolver REMOVEs it; response returns null).
        commit('addOrUpdateOrganisation', Object.assign({ deletedAt: null }, restoreOrganisation))
        commit('snackbar/showMessage', { content: 'Organization restored' }, { root: true })

        return restoreOrganisation
      } catch (e) {
        alert(e.message)
        console.log(e)
      }
    },

    // Permanent delete: snapshots the org to S3 then cascade-hard-deletes it. Only
    // valid for an already-archived org (the Lambda enforces that). Irreversible
    // from the UI — restorable only from the S3 snapshot.
    async purgeOrganisation ({ commit }, { id }) {
      try {
        const { purgeOrganisation } = await client.query({
          operationName: 'purgeOrganisation',
          query: PurgeOrganisation,
          variables: { id }
        })
        commit('deleteOrganisation', { id })
        commit('snackbar/showMessage', { content: 'Organization permanently deleted (snapshot saved)' }, { root: true })

        return purgeOrganisation
      } catch (e) {
        alert(e.message)
        console.log(e)
      }
    },

    async listOrganisations ({ commit }) {
      const { listOrganisations } = await client.query({
        operationName: 'listOrganisations',
        query: ListOrganisations
      })
      // Defensive: drop any null/malformed rows so a single bad record degrades
      // gracefully instead of breaking the whole list (or crashing a consumer).
      const orgs = (listOrganisations || []).filter((org) => org && org.id)
      commit('setOrganisations', orgs)
    },

    async updateOrganisationAiSettings ({ commit }, { id, allowedAiVersions, defaultAiVersion }) {
      try {
        const { updateOrganisationAiSettings } = await client.query({
          operationName: 'updateOrganisationAiSettings',
          query: UpdateOrganisationAiSettings,
          variables: { id, allowedAiVersions, defaultAiVersion }
        })
        commit('addOrUpdateOrganisation', updateOrganisationAiSettings)
        commit('snackbar/showMessage', { content: 'AI settings saved' }, { root: true })
        return updateOrganisationAiSettings
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to save AI settings', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    },

    async adminUpdateOrganisationAiSettings ({ commit }, { id, adminAllowedAiVersions }) {
      try {
        const { adminUpdateOrganisationAiSettings } = await client.query({
          operationName: 'adminUpdateOrganisationAiSettings',
          query: AdminUpdateOrganisationAiSettings,
          variables: { id, adminAllowedAiVersions }
        })
        commit('addOrUpdateOrganisation', adminUpdateOrganisationAiSettings)
        commit('snackbar/showMessage', { content: 'Admin AI settings saved' }, { root: true })
        return adminUpdateOrganisationAiSettings
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to save admin AI settings', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    },

    async adminUpdateOrganisationAccount ({ commit }, account) {
      try {
        const { adminUpdateOrganisationAccount } = await client.query({
          operationName: 'adminUpdateOrganisationAccount',
          query: AdminUpdateOrganisationAccount,
          variables: account
        })
        commit('addOrUpdateOrganisation', adminUpdateOrganisationAccount)
        commit('snackbar/showMessage', { content: 'Account & license saved' }, { root: true })
        return adminUpdateOrganisationAccount
      } catch (e) {
        commit('snackbar/showMessage', { content: e.message || 'Failed to save account & license', color: 'error' }, { root: true })
        console.error(e)
        return null
      }
    }
  }
}
